// THE PHONE, FOR THE WHOLE APP (Stage 4.8f.4b, guide §6.12; mockup "Where the phone lives"). One line for the session — held here,
// above every page, so moving between pages never drops a call — and one phone docked beside the page (minimised to the top bar's
// button), for anyone with an extension: an agent, and the owner once they have their own line (the owner's F1, ownerLine.js).
//   · usePhone() (phoneContext.js) → { available, dial(number, name), setOpen() } — every number in the app calls through it (CallButton.jsx);
//   · subscribe(fn) → the agent dashboard hears the call in focus and the calls that end (its CRM panel, its wrap-up), and gives
//     back what the ringing card shows (setRingContext: the briefing, the hand-off, the caller profile, the ticket action);
//   · the incoming card and the audio are here, so a call rings whether the phone is open or not.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../services/axiosInstance";
import { callerName } from "../pages/handoff/briefingMeta";
import { checkNumber, recentCalls } from "../pages/agent/phoneApi";
import usePhoneLine from "./usePhoneLine";
import PhoneDock from "./PhoneDock";
import PhoneIncoming from "./PhoneIncoming";
import { PhoneCtx } from "./phoneContext";


const DOCK_W = 380;
const OPEN_KEY = "teleredo.phone.open";
// Remembered per browser; never chosen yet: open beside a wide page, minimised on a narrow one (it would cover the page).
const wideNow = () => typeof window !== "undefined" && window.innerWidth >= 1200;
const readOpen = () => { try { const v = localStorage.getItem(OPEN_KEY); return v === null ? wideNow() : v !== "0"; } catch { return wideNow(); } };
const writeOpen = (v) => { try { localStorage.setItem(OPEN_KEY, v ? "1" : "0"); } catch { /* private window */ } };

export default function PhoneProvider({ children }) {
  const { t } = useTranslation("phone");
  const user = useSelector((s) => s.auth.user);
  const wide = useMediaQuery("(min-width:1200px)");
  const [me, setMe] = useState(null);               // { owner, extension, sipPassword, name }
  const [making, setMaking] = useState(false);
  const [makeError, setMakeError] = useState("");
  const [status, setStatusState] = useState("available");
  const [statusSaving, setStatusSaving] = useState(false);
  const [recent, setRecent] = useState(null);
  const [recentKey, setRecentKey] = useState(0);
  const [open, setOpenState] = useState(readOpen);
  const [prefill, setPrefill] = useState({ number: "", seq: 0 });
  const [ring, setRing] = useState({ briefing: null, handoff: null, callerProfile: null, onOpenTicket: null });
  const listeners = useRef(new Set());

  const setOpen = useCallback((v) => { setOpenState(v); writeOpen(v); }, []);

  // Who this phone is (an agent, or the owner — with their line or not yet).
  const loadMe = useCallback(() => axiosInstance.get("/phone/me").then((r) => setMe(r.data)).catch(() => setMe({ owner: user?.accountType !== "subaccount", extension: null })), [user?.accountType]);
  useEffect(() => { if (user) loadMe(); }, [user, loadMe]);
  const hasLine = !!me?.extension;
  const hasPhone = hasLine || !!me?.owner;   // the owner sees the phone to make their line

  // Their status (what the engine reads to ring them or not).
  useEffect(() => { if (hasLine) axiosInstance.get("/phone/status").then((r) => setStatusState(r.data.status || "available")).catch(() => {}); }, [hasLine]);
  const setStatus = useCallback(async (s) => {
    setStatusSaving(true);
    try { await axiosInstance.put("/phone/status", { status: s }); setStatusState(s); } catch { /* stays as it was */ }
    finally { setStatusSaving(false); }
  }, []);

  // Their recent calls (the server's): every minute and after each call.
  useEffect(() => {
    if (!hasLine) return undefined;
    const load = () => recentCalls().then(setRecent).catch(() => setRecent((d) => d ?? { calls: [], callbacks: [] }));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [hasLine, recentKey]);

  const emit = useCallback((ev) => { for (const fn of listeners.current) { try { fn(ev); } catch (e) { console.error("[phone] listener:", e); } } }, []);
  const onFocusChange = useCallback((info) => emit({ type: "focus", info }), [emit]);
  const onCallEnded = useCallback((info) => { setRecentKey((k) => k + 1); emit({ type: "ended", info }); }, [emit]);

  const line = usePhoneLine({ extension: me?.extension, password: me?.sipPassword, enabled: hasLine, onFocusChange, onCallEnded, notifyText: { title: t("incomingCall") } });
  const ready = line.reg === "registered" && line.mic !== "blocked" && line.mic !== "none";
  const ringingAlone = line.calls.length === 1 && line.calls[0].state === "ringing" ? line.calls[0] : null;
  const busy = line.calls.some((c) => c.state !== "ringing");

  // Answering opens the phone (its controls are where the call is).
  const answer = useCallback((id) => { setOpen(true); line.answer(id); }, [line, setOpen]);

  // Click-to-call: checked first — allowed → dialled at once; not (or the line not ready, or already on a call) → the keypad, with the
  // number in it and the reason under it.
  const dial = useCallback(async (number, name = "") => {
    const n = String(number ?? "").replace(/[^\d+*#]/g, "");
    if (!hasLine || !n) return false;
    setOpen(true);
    if (ready && !busy) {
      const c = await checkNumber(n).catch(() => null);
      if (c?.ok) return line.dial(n, name || c.contact?.name || c.name || "");
    }
    setPrefill((p) => ({ number: n, seq: p.seq + 1 }));
    return false;
  }, [hasLine, ready, busy, line, setOpen]);

  const makeLine = useCallback(async () => {
    setMaking(true); setMakeError("");
    try { const r = await axiosInstance.post("/phone/me/line"); setMe(r.data); }
    catch (e) { setMakeError(e?.response?.data?.reason || "failed"); }
    finally { setMaking(false); }
  }, []);

  const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
  const setRingContext = useCallback((r) => setRing((prev) => ({ ...prev, ...r })), []);

  const docked = hasPhone && open && wide;
  // The page makes room for the docked phone (SideBar.jsx reads --phone-dock-w).
  useEffect(() => {
    document.documentElement.style.setProperty("--phone-dock-w", docked ? `${DOCK_W + 24}px` : "0px");
    return () => document.documentElement.style.setProperty("--phone-dock-w", "0px");
  }, [docked]);

  const value = useMemo(() => ({
    available: hasLine, hasPhone, ready, open, setOpen, dial, subscribe, setRingContext, line, me, status,
  }), [hasLine, hasPhone, ready, open, setOpen, dial, subscribe, setRingContext, line, me, status]);

  const ringName = callerName(ring.briefing, ring.callerProfile);

  return (
    <PhoneCtx.Provider value={value}>
      {children}
      {hasPhone && open && (
        <Box component="aside" aria-label={t("phone")} sx={wide
          ? { position: "fixed", zIndex: 1150, top: 76, bottom: 12, insetInlineEnd: 12, width: DOCK_W, display: "flex", flexDirection: "column" }
          : { position: "fixed", zIndex: 1300, top: 64, bottom: 0, insetInlineStart: 0, insetInlineEnd: 0, display: "flex", flexDirection: "column", background: "var(--surface-page)", p: "8px" }}>
          <PhoneDock line={line} me={me} ready={ready} status={status} onStatusChange={setStatus} statusSaving={statusSaving}
            recent={recent} ringName={ringName} onOpenTicket={ring.onOpenTicket} onMinimise={() => setOpen(false)}
            prefill={prefill} onMakeLine={makeLine} making={making} makeError={makeError} />
        </Box>
      )}
      {hasLine && (
        <PhoneIncoming call={ringingAlone} name={ringName} address={ring.callerProfile?.address ?? null} briefing={ring.briefing} handoff={ring.handoff}
          micBlocked={line.mic === "blocked"} onAnswer={answer} onDecline={line.hangup} />
      )}
      {hasLine && <audio ref={line.audioRef} autoPlay style={{ display: "none" }} />}
    </PhoneCtx.Provider>
  );
}
